import React, { forwardRef } from 'react';

import { defineMessages, type MessageDescriptor, useIntl } from 'react-intl-next';

import { cssMap } from '@atlaskit/css';
import Lozenge from '@atlaskit/lozenge';
import { Anchor, Box, Flex, Inline, Pressable, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import { type AIActionStatus } from '@atlassian/ai-smart-button-api';

export const i18n = defineMessages({
	aiActionStatusBeta: {
		id: 'ai-smart-button.ai-actions.item.betaLozenge',
		defaultMessage: 'BETA',
		description: 'Lozenge text to indicate that the action is in beta stage',
	},
	aiActionStatusNew: {
		id: 'ai-smart-button.ai-actions.item.newLozenge',
		defaultMessage: 'NEW',
		description: 'Lozenge text to indicate that the action is new',
	},
});

const styles = cssMap({
	buttonContentStyles: {
		display: 'flex',
		alignItems: 'center',
		gap: token('space.150'),
		paddingInline: token('space.150'),
	},

	inlineItemStyles: {
		color: token('color.text.subtle'),
		gap: token('space.100'),
	},

	iconBoxStyles: {
		width: '32px',
		height: '32px',
		minWidth: '32px',
		borderStyle: 'solid',
		borderWidth: token('border.width'),
		borderColor: token('color.border'),
		borderRadius: token('border.radius'),
	},

	pressableStyles: {
		backgroundColor: token('color.background.neutral.subtle'),
		width: '100%',
		paddingLeft: token('space.100'),
		paddingRight: token('space.100'),
		paddingTop: token('space.075'),
		paddingBottom: token('space.075'),
		textDecoration: 'none',
		color: token('color.text.subtle'),

		'&:hover': {
			backgroundColor: token('color.background.neutral.subtle.hovered'),
			borderRadius: token('border.radius'),
			textDecoration: 'none',
			color: token('color.text.subtle'),
		},
	},
});

const statusLozengeMessages: Record<AIActionStatus, MessageDescriptor> = {
	beta: i18n.aiActionStatusBeta,
	new: i18n.aiActionStatusNew,
};

const BaseActionItem = ({
	title,
	icon,
	status,
}: {
	title: string;
	icon: React.ReactElement;
	status?: AIActionStatus;
}) => {
	const { formatMessage } = useIntl();
	const statusText =
		(status &&
			(statusLozengeMessages[status] ? formatMessage(statusLozengeMessages[status]) : null)) ||
		status;
	return (
		<Box xcss={styles.buttonContentStyles}>
			<Flex justifyContent="center" alignItems="center" xcss={styles.iconBoxStyles}>
				{icon}
			</Flex>
			<Inline xcss={styles.inlineItemStyles} alignBlock="center">
				<Text>{title}</Text>
				{statusText && <Lozenge appearance="default">{statusText}</Lozenge>}
			</Inline>
		</Box>
	);
};

type DynamicSurfaceActionItemProps = {
	title: string;
	icon: React.ReactElement;
	onClick?: () => void;
	href?: string;
	status?: AIActionStatus;
};

export const DynamicSurfaceActionItem = forwardRef<
	HTMLButtonElement,
	DynamicSurfaceActionItemProps
>(({ title, icon, onClick, href, status }, ref) => {
	const item = <BaseActionItem title={title} icon={icon} status={status} />;

	return href ? (
		<Anchor href={href} target="_blank" onClick={onClick} xcss={styles.pressableStyles}>
			{item}
		</Anchor>
	) : (
		<Pressable xcss={styles.pressableStyles} onClick={onClick} ref={ref}>
			{item}
		</Pressable>
	);
});
